import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { map, concatMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AssetInfo } from '@app/@shared/models/pdf-asset-info';
import { EntitlementService } from '@app/@shared/services/entitlement/entitlement.service';
import { MetaService } from '@app/@shared/services/meta.service';
import { ReportService } from '@app/@shared/services/report.service';
import { AssetModel } from '@app/@shared/models/asset.model';
import {
  Analysis,
  AssetClassificationType,
  ReportMimeType
} from '@app/@shared/models/reverse-engineering/analysis.models';
import { moduleNameFromUrl } from '@app/@shared/utils/common';
import { TelemetryEvent } from '@app/@shared/models/telemetry-event';
import { TelemetryEventName } from '@app/@shared/enums/telemetry-event-name';
import { RecentViewHistoryService } from '@app/@shared/services/recent-view-history/recent-view-history.service';
import {
  UserViewHistoryItemSource,
  UserViewHistoryItemType,
} from '@app/@shared/models/user-view-history/user-view-history-items';
import { DeepLinkingService } from '@app/@shared/services/deep-linking.service';

@UntilDestroy()
@Component({
    selector: 'app-document-pdf-loader',
    templateUrl: './document-pdf-loader.component.html',
    styleUrls: ['./document-pdf-loader.component.scss'],
    standalone: false
})
export class DocumentPdfLoaderComponent {

  @Input() set analysisCode(code: string) {
    this.getCurrentAnalysis(code);
  }
  @Input() reportId: string;
  @Input() channelId: string;
  @Input() subscriptionId: string;
  @Input() isAnnotated = false;

  @Output() closeAssetViewer = new EventEmitter<void>();

  public pdfAssetInfo: AssetInfo;
  public showDownloadButton = false;
  public signedUrl: string;

  constructor(
    private readonly entitlementService: EntitlementService,
    private readonly metaService: MetaService,
    private readonly reportService: ReportService,
    private readonly router: Router,
    private readonly viewHistoryService: RecentViewHistoryService,
    private readonly deepLinkingService: DeepLinkingService,
  ) {}

  getCurrentAnalysis(analysisCode: string): void {
    if (analysisCode) {
      this.metaService.getReportAndTopAssetGroupByCode(analysisCode).pipe(
        map(({ report }) => report),
        concatMap((report) => this.metaService.getAssetFromClId(this.reportId).pipe(map(asset => [report, asset]))),
        concatMap(([report, asset]) => forkJoin([
          this.reportService.getReportURL(asset.contentLakeObjectId),
          this.entitlementService.getReportEntitlements(report.id),
          of(asset),
          of(report),
        ])),
        untilDestroyed(this),
      ).subscribe(
        ([ assetInfo, {download_enabled}, asset, report ]) => {
          const htmlAsset = assetInfo.find(asset => asset.type === AssetClassificationType.HTML);
          const pdfAsset = assetInfo.find(asset => asset.type === AssetClassificationType.XOD || asset.mimetype === "application/pdf");
          this.pdfAssetInfo = pdfAsset;
          // If the asset is an HTML asset, we need to sign the URL
          if(htmlAsset){
            this.signedUrl = htmlAsset.url;
          }

          this.showDownloadButton = download_enabled.pdf;
          this.trackPDFViewEvent(asset, report, this.subscriptionId, this.channelId);
          this.sendViewHistoryEvent(asset, report);
        },
        (err: any) => {
          if (err.status === 403) {
            this.deepLinkingService.redirectTo403();
            return of(null);
          }
        }
      );
    }
  }

  downloadPdf(): void {
    this.reportService.downloadPdf(this.pdfAssetInfo).subscribe();
  }

  onCloseAssetViewer(): void {
    this.closeAssetViewer.emit();
  }

  private trackPDFViewEvent(asset: AssetModel, report: Analysis, subscriptionId: string, channelId: string): void {
    const payload: any = {
      artifactName: asset.name,
      artifactType: ReportMimeType.PDF,
      channelId,
      downloadSizeBytes: 0,
      reportCode: report.code,
      reportId: asset.id,
      reportTitle: report.name,
      subscriptionId,
    };
    const applicationModule = moduleNameFromUrl(this.router.url);
    const event = new TelemetryEvent(TelemetryEventName.EVENT_VIEW_IN_BROWSER, payload, applicationModule);
    this.metaService.postTelemetryEvent(event).subscribe();
  }

  private sendViewHistoryEvent(asset: AssetModel, report: Analysis): void {
    const moduleId = window.location.toString().split('/')[3].trim();
    const channelId = window.location.toString().split('/')[4].trim();
    this.viewHistoryService.postRecentViewHistory({
      itemName: asset.name.substring(0, asset.name.lastIndexOf('.')),
      itemType: UserViewHistoryItemType.PDF,
      itemSource: UserViewHistoryItemSource.META,
      itemId: asset.id,
      itemPublicationDate: report.analysisEndDate ? new Date(report.analysisEndDate) : null,
      channelSfId: channelId,
      subscriptionSfId: this.subscriptionId,
      moduleId,
      clObjectId: asset.contentLakeObjectId,
      payload: {
        reportId: report.id,
        reportCode: report.code,
        isAnnotated: this.isAnnotated,
        container: 'analysis-view',
      },
    }).pipe(untilDestroyed(this)).subscribe();
  }

}
